import { fetchAuthSession, signOut } from "aws-amplify/auth";
import {
  cognitoLoginSuccess,
  setCognitoConfig,
  setUpgradePlanFlag,
  setUserRole,
  storeSession
} from "src/page/auth/loginSlice";
import store from "src/page/store";
import { setShowSubscriptionModal } from "src/page/plans/plansSlice";
import { getConfig, isCookieAuthMode } from "src/config/Config";
import { logoutSession, refreshAuthSession } from "src/network/authService";
import { Transaction } from "src/page/transactions/transactionSlice";
import axios from "axios";
import swal from "sweetalert2";
import { checkUserNameAvailability } from "src/network/graphql/userService";
import { CardAccessType, SpendFrequency, User } from "src/types/commonTypes";
import { EMPTY_STRING, WALLET_TRANSACTION } from "src/components/constant/constant";
import { useEffect, useRef, useState } from "react";
import { formatFullName, getDecimalNumber } from "./formatUtils";
import { sanitizeName } from "./validationUtils";
import { API_ERROR_EVENTS, enrichEventProperties, trackEvent } from "src/services/analytics";

const REVOKE_TIMEOUT_MS = 3000;

export const refreshSession = async (refreshToken?: string) => {
  const dispatch = store.dispatch;
  const config = getConfig();
  const { auth } = store.getState();

  dispatch(setUpgradePlanFlag(false));

  if (isCookieAuthMode()) {
    try {
      const session = await refreshAuthSession();

      storeSession(session);
      return session;
    } catch (error) {
      console.error("Error refreshing session:", error);
      return undefined;
    }
  }

  const isSSOUser = !auth.changePasswordPermission;

  if (isSSOUser && refreshToken) {
    return new Promise((resolve, reject) => {
      const response = axios.post(
        `${config.cognitoConfig.baseUrl}/oauth2/token`,
        new URLSearchParams({
          grant_type: "refresh_token",
          refresh_token: refreshToken,
          client_id: config.COGNITO_CLIENT_ID
        }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded"
          }
        }
      );

      if (response === null || response === undefined) return reject({ message: "User session expired" });

      response
        .then((res) => {
          const accessToken = res.data.access_token;
          const idToken = res.data.id_token;

          const payload = JSON.parse(atob(idToken.split(".")[1]));

          dispatch(setUserRole(payload.current_role));
          const organisations = JSON.parse(payload.available_relations);

          const currentOrganisation = organisations.find(
            (org: { organisation_id: string }) => org.organisation_id === payload.current_organisation
          );

          dispatch(
            setCognitoConfig({
              organisations,
              currentOrganisation: currentOrganisation
            })
          );

          const structuredSession = {
            idToken: idToken,
            accessToken: accessToken,
            refreshToken: refreshToken
          };

          dispatch(cognitoLoginSuccess(structuredSession));
          return resolve(structuredSession);
        })
        .catch((err) => {
          return reject(err);
        });
    });
  }

  try {
    const session = await fetchAuthSession({ forceRefresh: true });

    if (!session.tokens?.idToken || !session.tokens?.accessToken) {
      throw new Error("Invalid session tokens");
    }

    const idToken = session.tokens.idToken.toString();
    const accessToken = session.tokens.accessToken.toString();
    const payload = session.tokens.accessToken.payload;

    if (payload.current_role) {
      dispatch(setUserRole(payload.current_role as string));
      const organisations = JSON.parse(payload.available_relations as string);

      const currentOrganisation = organisations.find(
        (org: { organisation_id: string }) => org.organisation_id === payload.current_organisation
      );

      dispatch(
        setCognitoConfig({
          organisations,
          currentOrganisation: currentOrganisation
        })
      );
    }

    const structuredSession = {
      idToken,
      accessToken,
      refreshToken
    };

    dispatch(cognitoLoginSuccess(structuredSession));
    return structuredSession;
  } catch (error) {
    console.error("Error refreshing session:", error);
  }
};

// Best-effort and bounded: never throws, so logout always completes.
export const revokeSession = async (refreshToken?: string) => {
  let timer: ReturnType<typeof setTimeout> | undefined;

  try {
    const { COGNITO_CLIENT_ID, cognitoConfig } = getConfig();
    const isSSOUser = !store.getState().auth.changePasswordPermission;

    // Bare axios, so a failed revoke never reaches the 401 interceptors.
    const revokeHostedUiToken = () =>
      axios.post(
        `${cognitoConfig.baseUrl}/oauth2/revoke`,
        new URLSearchParams({
          token: refreshToken ?? EMPTY_STRING,
          client_id: COGNITO_CLIENT_ID
        }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded"
          },
          timeout: REVOKE_TIMEOUT_MS
        }
      );

    let revoke: Promise<unknown>;

    if (isCookieAuthMode()) {
      revoke = logoutSession();
    } else if (isSSOUser && refreshToken) {
      revoke = revokeHostedUiToken();
    } else {
      revoke = signOut();
    }

    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error("Token revoke timed out")), REVOKE_TIMEOUT_MS);
    });

    await Promise.race([revoke, timeout]);
  } catch (error) {
    console.error("Error revoking session:", error);
    trackEvent(
      API_ERROR_EVENTS.TOKEN_REVOKE_FAILED,
      enrichEventProperties({
        error_message: error instanceof Error ? error.message : "Token revoke failed"
      })
    );
  } finally {
    clearTimeout(timer);
  }
};

export const createUsername = (input: { firstName: string; lastName: string }) => {
  const random = Math.floor(100000000 + Math.random() * 900000000);

  return `${input.firstName}_${input.lastName}_${random}`.replace(/\s+/g, "_").toLowerCase();
};

export const suggestUsername = (firstName: string, lastName: string): string => {
  const randomNumber = Math.floor(Math.random() * 900) + 100;

  return `${firstName}.${lastName}${randomNumber}`.toLowerCase();
};

export const generateUsername = async (firstName: string, lastName: string): Promise<string> => {
  let attempts = 0;
  let generatedUsername = EMPTY_STRING;

  do {
    generatedUsername = suggestUsername(firstName, lastName);

    const isUserNameAvailable = await checkUserNameAvailability(generatedUsername);

    if (isUserNameAvailable.data.is_username_available) {
      return generatedUsername;
    }

    attempts++;
  } while (attempts < 3);

  return EMPTY_STRING;
};

export const mapApiUserToUser = (apiUser: any, extras?: Partial<User>): User => {
  const rawFirst = apiUser?.firstName ?? apiUser?.first_name ?? apiUser?.attributes?.first_name ?? apiUser?.name;
  const rawLast = apiUser?.lastName ?? apiUser?.last_name ?? apiUser?.attributes?.last_name ?? apiUser?.family_name;

  const firstName = sanitizeName(rawFirst);
  const lastName = sanitizeName(rawLast);
  const fullName = formatFullName(firstName, lastName);

  if (!firstName || !lastName) {
    console.warn("mapApiUserToUser: received missing/invalid name fields", {
      userId: apiUser?.user_id ?? apiUser?.id ?? apiUser?.sub,
      rawFirst,
      rawLast
    });
  }

  return {
    id: apiUser?.user_id ?? apiUser?.id ?? apiUser?.sub ?? EMPTY_STRING,
    displayId: apiUser?.display_id ?? apiUser?.displayId ?? EMPTY_STRING,
    cognitoUserName:
      apiUser?.cognito_username ?? apiUser?.cognitoUserName ?? apiUser?.["cognito:username"] ?? EMPTY_STRING,
    username: apiUser?.username,
    firstName,
    lastName,
    fullName,
    email: apiUser?.email,
    contact: apiUser?.phone ?? apiUser?.phone_number,
    createdAt: apiUser?.created_at ?? apiUser?.createdAt,
    ...extras
  };
};

export type ApiUser = {
  user_id?: string;
  display_id?: string;
  username?: string;
  attributes?: {
    first_name?: string;
    last_name?: string;
    address?: User["address"];
  };
  email?: string;
  current_organisation_role?: string;
};

export const mapApiUser = (user: ApiUser): User => {
  const firstName = user?.attributes?.first_name ?? EMPTY_STRING;
  const lastName = user?.attributes?.last_name ?? EMPTY_STRING;
  const fullName = ([firstName, lastName].filter(Boolean).join(" ") || user?.email) ?? "Unknown";

  return {
    id: user?.user_id ?? EMPTY_STRING,
    displayId: user?.display_id ?? EMPTY_STRING,
    cognitoUserName: user?.username ?? EMPTY_STRING,
    fullName,
    firstName,
    lastName,
    email: user?.email,
    role: user?.current_organisation_role,
    address: user?.attributes?.address
  };
};

export const removeNull = (obj: object) => {
  const newObj: { [_key: string]: any } = {};

  Object.entries(obj).forEach(([key, value]) => {
    if (value != null || value != undefined) {
      newObj[key] = value;
    }
  });
  return newObj;
};

export const getTransactionValue = (transaction: Transaction) => {
  const amount = "$" + getDecimalNumber(transaction?.data?.amount);
  let value;
  let amountColor;

  if (transaction?.name === "NOVATTI_CARD_TRANSACTION") {
    if (transaction?.data?.transType === "CASH") {
      value = transaction?.data?.description;
    } else {
      value = transaction?.data?.merchantName;
    }
    amountColor = "#ff0000";
  } else if (transaction?.name === "WALLET_TRANSACTION") {
    if (transaction?.data?.type === "DEPOSIT") {
      value = "Deposit SpendAble";
      if (transaction?.data?.amount > 0) {
        amountColor = "#008000";
      } else {
        amountColor = "#ff0000";
      }
    } else if (transaction?.data?.type === "TRANSFER") {
      if (transaction?.data?.side === "OUT") {
        value = transaction?.data?.destination_wallet?.name;
        amountColor = "#ff0000";
      } else if (transaction?.data?.side === "IN") {
        value = transaction?.data?.source_wallet?.name;
        amountColor = "#008000";
      }
    } else if (transaction?.data?.type === "ADJUSTMENT") {
      value = transaction?.data?.merchantName || "Adjustment SpendAble";
      if (transaction?.data?.amount > 0) {
        amountColor = "#008000";
      } else {
        amountColor = "#ff0000";
      }
    }
  } else if (transaction?.name === "CASH_TRANSACTION") {
    value = transaction?.data?.merchantName;
    // A cash reversal references the receipt it reverses — money coming back, not going out.
    // Checked inline rather than via the reversal helper, which imports from this barrel.
    amountColor = transaction?.data?.reference_link_id ? "#008000" : "#ff0000";
  }
  return {
    value,
    amount,
    amountColor
  };
};

export type SettlementCorrection = {
  settledAt?: string;
  authorisedAmount: number;
  settledAmount: number;
  /** Positive when Novatti settled for less than we authorised, so the difference went back to the wallet. */
  delta: number;
};

/**
 * A Novatti settlement correction leaves the authorised amount on `data.amount`
 * and records what actually settled on the activity attributes. The difference is
 * posted as its own adjustment activity, so callers must keep showing the
 * authorised figure as the primary amount and treat this as a secondary marker.
 */
export const getSettlementCorrection = (transaction: Transaction): SettlementCorrection | null => {
  const delta = transaction?.settlement_delta;
  const settled = transaction?.settled_amount;

  if (typeof delta !== "number" || delta === 0 || typeof settled !== "number") return null;

  const authorised = transaction?.data?.amount;

  return {
    settledAt: transaction?.settled_at,
    authorisedAmount: typeof authorised === "number" ? Math.abs(authorised) : Math.abs(settled + delta),
    settledAmount: Math.abs(settled),
    delta
  };
};

export const getTransactionTitle = (transaction: Transaction): string => {
  const data = transaction?.data;
  const amount = data?.amount;
  const merchant = data?.merchantName || EMPTY_STRING;
  const wallet = data?.wallet?.name || EMPTY_STRING;

  if (amount == null) {
    if (merchant && wallet) return `${merchant} via ${wallet}`;
    return merchant || wallet || "Transaction";
  }

  const money = `$${getDecimalNumber(Math.abs(amount))}`;
  const positive = amount > 0;
  const clause = (preposition: string, name?: string) => (name ? `${preposition} ${name}` : EMPTY_STRING);
  const build = (verb: string, ...rest: string[]) => [verb, money, ...rest].filter(Boolean).join(" ");

  if (transaction?.name === WALLET_TRANSACTION) {
    if (data?.type === "DEPOSIT")
      return positive ? build("Deposited", clause("to", wallet)) : build("Withdrew", clause("from", wallet));
    if (data?.type === "ADJUSTMENT")
      return positive
        ? build("Credit adjustment of", clause("to", wallet))
        : build("Debit adjustment of", clause("from", wallet));
    if (data?.type === "TRANSFER" && data?.side === "IN")
      return build("Received", clause("from", data?.source_wallet?.name));
    if (data?.type === "TRANSFER" && data?.side === "OUT")
      return build("Transferred", clause("to", data?.destination_wallet?.name));
  }

  const isCredit = getTransactionValue(transaction).amountColor === "#008000";

  return build(isCredit ? "Received" : "Spent", clause(isCredit ? "from" : "at", merchant), clause("via", wallet));
};

export const calculateBudget = (
  balance: number = 0,
  spend_frequency: SpendFrequency,
  amountSpend?: number,
  budgetLimit?: number
): number => {
  if (!budgetLimit || spend_frequency === "NONE") return balance;
  if (amountSpend === undefined || amountSpend === null) return balance;

  const remaining = budgetLimit - amountSpend;

  return getDecimalNumber(Math.max(0, Math.min(remaining, balance)));
};

export const resolveCardAccessType = (accessType?: string): CardAccessType => {
  if (accessType === "SHARED" || accessType === "PRIVATE") {
    return accessType;
  }

  return "SHARED";
};

interface UpgradePlanProp {
  title: string;
  subTitle: string;
}

export const upgradePlan = ({ title, subTitle }: UpgradePlanProp) => {
  const dispatch = store.dispatch;

  swal
    .fire({
      icon: "warning",
      title: title,
      text: subTitle,
      showCancelButton: true,
      confirmButtonText: "Upgrade Plan",
      cancelButtonText: `I'll do it later.`,
      confirmButtonColor: "#0f172a"
    })
    .then((result) => {
      if (result.isConfirmed) {
        dispatch(setShowSubscriptionModal(true));
      }
    });
};

/**
 * Custom hook for typing animation effect
 * @param text - The text to animate
 * @param options - Configuration options for the animation
 * @returns The animated text string
 */
export const useTypingAnimation = (
  text: string,
  options?: {
    typingSpeed?: number;
    deletingSpeed?: number;
    pauseDelay?: number;
    startDelay?: number;
    enabled?: boolean;
    loop?: boolean;
  }
): string => {
  const {
    typingSpeed = 100,
    deletingSpeed = 50,
    pauseDelay = 2000,
    startDelay = 500,
    enabled = true,
    loop = true
  } = options || {};

  const [animatedText, setAnimatedText] = useState(text || EMPTY_STRING);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const typingStateRef = useRef({ currentIndex: 0, isDeleting: false });

  useEffect(() => {
    // Reset state when text or enabled changes
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingStateRef.current = { currentIndex: 0, isDeleting: false };

    if (!enabled || !text) {
      setAnimatedText(text || EMPTY_STRING);
      return;
    }

    const typeText = () => {
      const typingState = typingStateRef.current;

      if (typingState.isDeleting) {
        if (typingState.currentIndex > 0) {
          setAnimatedText(text.substring(0, typingState.currentIndex - 1));
          typingState.currentIndex--;
          typingTimeoutRef.current = setTimeout(typeText, deletingSpeed);
        } else {
          if (loop) {
            typingState.isDeleting = false;
            typingTimeoutRef.current = setTimeout(typeText, typingSpeed);
          } else {
            setAnimatedText(EMPTY_STRING);
          }
        }
      } else {
        if (typingState.currentIndex < text.length) {
          setAnimatedText(text.substring(0, typingState.currentIndex + 1));
          typingState.currentIndex++;
          typingTimeoutRef.current = setTimeout(typeText, typingSpeed);
        } else {
          if (loop) {
            typingTimeoutRef.current = setTimeout(() => {
              typingState.isDeleting = true;
              typeText();
            }, pauseDelay);
          } else {
            setAnimatedText(text);
          }
        }
      }
    };

    typingTimeoutRef.current = setTimeout(typeText, startDelay);

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingStateRef.current = { currentIndex: 0, isDeleting: false };
    };
  }, [text, typingSpeed, deletingSpeed, pauseDelay, startDelay, enabled, loop]);

  return animatedText || text || EMPTY_STRING;
};
